import { Component } from '@theme/component';

/**
 * Tab component for switching between content panels.
 * Supports keyboard navigation (Arrow keys, Home, End) and ARIA attributes.
 *
 * @extends Component
 */
class CustomTabsComponent extends Component {
  requiredRefs = ['container'];
  #active = 0;
  #abort = null;

  #moveTabElements() {
    const container = Array.isArray(this.refs?.container) ? this.refs.container[0] : this.refs?.container;
    if (!container || !(container instanceof HTMLElement)) return;
    const tablist = container.querySelector('.custom-tabs__tablist');
    const panels = container.querySelector('.custom-tabs__panels');
    const blockOutput = container.querySelector('.custom-tabs__block-output');
    if (!tablist || !panels || !blockOutput) return;
    blockOutput.querySelectorAll('.custom-tabs__tab-button').forEach((btn) => tablist.appendChild(btn));
    blockOutput.querySelectorAll('[role="tabpanel"]').forEach((panel) => panels.appendChild(panel));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#moveTabElements();
    const { tabs, panels } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#updateActiveTab();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
    }
  }

  #handleKeydown(/** @type {KeyboardEvent} */ e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);
    const navMap = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const delta = navMap[e.key];
    if (delta === undefined) return;

    e.preventDefault();
    const next = delta === tabs.length - 1 - i ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    this.#activateTab(next);
    tabs[next]?.focus();
  }

  #handleTabClick(/** @type {MouseEvent} */ e, index) {
    e.preventDefault();
    this.#activateTab(index);
  }

  #activateTab(index) {
    const { tabs, panels } = this.refs;
    if (!tabs || index < 0 || index >= tabs.length) return;

    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs?.entries() ?? []) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.setAttribute('aria-hidden', String(!isActive));
    }
  }
}

if (!customElements.get('custom-tabs-component')) {
  customElements.define('custom-tabs-component', CustomTabsComponent);
}
